// PKT-0916 · money derived from invoices + clients. Pure, no I/O.
//
// Grant ruled 2026-09-30: invoice creation is the source of truth for money.
// There is no placements table and no "applied" form; everything here is read
// from `invoices` (status, amount, due_date, paid_at) and `clients` (status,
// move_in_date). Test and synthetic clients — and their invoices — are
// excluded, as everywhere else.
//
// (B) AWAITING INVOICE — the "Send invoice" nudge:
//   · clients.move_in_date is today or earlier (a CALENDAR day, local). Only
//     the real date column: move_in_timeline is free text ("ASAP", "Sep"),
//     and a money nudge must not fire on a guess;
//   · status is applied or payment_received;
//   · the client has NO invoice that is sent, overdue or paid. A cancelled or
//     failed invoice is not an invoice the property owes on.
//   Oldest move-in first.
//
// (C) MONEY SUMMARY:
//   owed       status sent + overdue: count, $ total, oldest days past due_date
//              (null when none is past due — "none late", never 0).
//   collected  status paid, by paid_at: this calendar month, and last 90 days.
//              paid_at is when the invoice was MARKED paid in LN Pro.
//   awaiting   the (B) count.

import type { Client } from "../components/clients/types";
import { parseCalendarDate } from "./calendarDate";

export const OPEN_INVOICE_STATUSES = ["sent", "overdue"] as const;
export const INVOICED_STATUSES = ["sent", "overdue", "paid"] as const;
export const AWAITING_INVOICE_STATUSES = ["applied", "payment_received"] as const;

const DAY_MS = 86_400_000;

export interface MoneyInvoice {
  id: string;
  client_id: string | null;
  status: string;
  amount: number | string | null;
  due_date: string | null;
  paid_at: string | null;
  invoice_number?: string | null;
}

/** The minimum client shape this module reads. */
export type MoneyClient = Pick<Client, "id" | "status"> &
  Partial<Pick<Client, "move_in_date" | "is_test_client" | "is_synthetic">>;

export interface AwaitingInvoiceRow<C extends MoneyClient = Client> {
  client: C;
  moveIn: Date;
  /** Calendar days since move-in; 0 = today. */
  daysSinceMoveIn: number;
}

export interface MoneySummary {
  owed: { count: number; total: number; oldestDaysLate: number | null };
  collectedThisMonth: { count: number; total: number };
  collectedLast90: { count: number; total: number };
  awaitingInvoice: number;
}

function isReal(c: Partial<Pick<Client, "is_test_client" | "is_synthetic">>): boolean {
  return c.is_test_client !== true && c.is_synthetic !== true;
}

function calendarDaysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / DAY_MS);
}

function amountOf(inv: MoneyInvoice): number {
  const n = typeof inv.amount === "number" ? inv.amount : Number(inv.amount);
  return Number.isFinite(n) ? n : 0;
}

/** Client ids with an invoice that is sent, overdue or paid. */
export function invoicedClientIdsFrom(invoices: MoneyInvoice[]): Set<string> {
  const ids = new Set<string>();
  for (const inv of invoices) {
    if (inv.client_id && (INVOICED_STATUSES as readonly string[]).includes(inv.status)) {
      ids.add(inv.client_id);
    }
  }
  return ids;
}

export function awaitingInvoice<C extends MoneyClient>(
  clients: C[],
  invoicedClientIds: Set<string>,
  now: Date = new Date()
): AwaitingInvoiceRow<C>[] {
  const rows: AwaitingInvoiceRow<C>[] = [];
  for (const client of clients) {
    if (!isReal(client)) continue;
    if (!(AWAITING_INVOICE_STATUSES as readonly string[]).includes(client.status)) continue;
    if (invoicedClientIds.has(client.id)) continue;
    if (!client.move_in_date) continue;
    const moveIn = parseCalendarDate(client.move_in_date);
    if (Number.isNaN(moveIn.getTime())) continue;
    const daysSinceMoveIn = calendarDaysBetween(moveIn, now);
    if (daysSinceMoveIn < 0) continue;
    rows.push({ client, moveIn, daysSinceMoveIn });
  }
  rows.sort(
    (a, b) => b.daysSinceMoveIn - a.daysSinceMoveIn || a.client.id.localeCompare(b.client.id)
  );
  return rows;
}

export function buildMoneySummary(
  invoices: MoneyInvoice[],
  clients: MoneyClient[],
  now: Date = new Date()
): MoneySummary {
  const excluded = new Set(clients.filter((c) => !isReal(c)).map((c) => c.id));
  const real = invoices.filter((inv) => !(inv.client_id && excluded.has(inv.client_id)));

  const owed = { count: 0, total: 0, oldestDaysLate: null as number | null };
  const collectedThisMonth = { count: 0, total: 0 };
  const collectedLast90 = { count: 0, total: 0 };
  const cutoff90 = now.getTime() - 90 * DAY_MS;

  for (const inv of real) {
    if ((OPEN_INVOICE_STATUSES as readonly string[]).includes(inv.status)) {
      owed.count += 1;
      owed.total += amountOf(inv);
      if (inv.due_date) {
        const late = calendarDaysBetween(parseCalendarDate(inv.due_date), now);
        if (late > 0 && (owed.oldestDaysLate === null || late > owed.oldestDaysLate)) {
          owed.oldestDaysLate = late;
        }
      }
    } else if (inv.status === "paid" && inv.paid_at) {
      const paid = new Date(inv.paid_at);
      if (Number.isNaN(paid.getTime())) continue;
      if (paid.getFullYear() === now.getFullYear() && paid.getMonth() === now.getMonth()) {
        collectedThisMonth.count += 1;
        collectedThisMonth.total += amountOf(inv);
      }
      if (paid.getTime() >= cutoff90) {
        collectedLast90.count += 1;
        collectedLast90.total += amountOf(inv);
      }
    }
  }

  const round = (n: number) => Math.round(n * 100) / 100;
  owed.total = round(owed.total);
  collectedThisMonth.total = round(collectedThisMonth.total);
  collectedLast90.total = round(collectedLast90.total);

  return {
    owed,
    collectedThisMonth,
    collectedLast90,
    awaitingInvoice: awaitingInvoice(clients, invoicedClientIdsFrom(real), now).length,
  };
}

/** Count only: current loaded records, not historical AR or a remaining balance. */
export interface OverdueOpenInvoiceCount {
  asOfDate: string;
  loadedCount: number;
  openCount: number;
  overdueCount: number;
  dueDateReviewCount: number;
  paymentReviewCount: number;
  excludedTestCount: number;
  clientCoverageUnknownCount: number;
}

/** Postgres date contract: reject malformed dates AND normalized dates such as Feb 30. */
function validInvoiceDay(value: string | null | undefined): value is string {
  if (typeof value !== "string" || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value) || value.startsWith("0000")) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function countOverdueOpenInvoices(
  invoices: MoneyInvoice[],
  clients: MoneyClient[],
  now: Date
): OverdueOpenInvoiceCount {
  if (!Number.isFinite(now.getTime())) throw new Error("Invalid invoice evaluation date");
  const asOfDate = `${String(now.getFullYear()).padStart(4, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (!validInvoiceDay(asOfDate)) throw new Error("Invalid invoice evaluation date");
  const clientsById = new Map(clients.map(client => [client.id, client]));
  const result: OverdueOpenInvoiceCount = {
    asOfDate, loadedCount: invoices.length, openCount: 0, overdueCount: 0,
    dueDateReviewCount: 0, paymentReviewCount: 0, excludedTestCount: 0,
    clientCoverageUnknownCount: 0,
  };
  for (const invoice of invoices) {
    if (!(OPEN_INVOICE_STATUSES as readonly string[]).includes(invoice.status)) continue;
    const client = invoice.client_id ? clientsById.get(invoice.client_id) : undefined;
    if (client && !isReal(client)) {
      result.excludedTestCount += 1;
      continue;
    }
    result.openCount += 1;
    // Invoice/client SELECT visibility can differ. Never claim all test records
    // were excluded when the related client or its flags are unavailable.
    if (!client || typeof client.is_test_client !== "boolean" || typeof client.is_synthetic !== "boolean") {
      result.clientCoverageUnknownCount += 1;
    }
    // A non-null paid_at on an open status conflicts with the status writer's
    // contract. Review it; do not treat it as either confirmed paid or overdue.
    if (invoice.paid_at != null) {
      result.paymentReviewCount += 1;
    } else if (!validInvoiceDay(invoice.due_date)) {
      result.dueDateReviewCount += 1;
    } else if (invoice.due_date < asOfDate) {
      result.overdueCount += 1;
    }
  }
  return result;
}
